import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import { TEAM_NAME_TAKEN_MESSAGE } from 'library/helpers/teamName';
import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { accessActions } from 'reduxStore/access/slice';
import { Notification } from 'zComponents/atoms';
import { watchActivity } from '../_shared/activity';
import { teamsActions } from './slice';

// A refused team create/rename: the duplicate-name 409 by its errorCode, else the server's own reason.
export const teamErrorText = (res, fallback) =>
	res?.errorCode === 'TEAM_NAME_TAKEN' ? TEAM_NAME_TAKEN_MESSAGE : apiFailureMessage(res, fallback);

// Fetches teams for the active scope (orgID + siteID from selectedProfile)
export function* getTeams() {
	const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
	const query = siteID !== 'all' ? { siteID } : {};

	// Set loading on target bucket (no saga listens to loadTeamsStart — no loop)
	yield put(teamsActions.loadTeamsStart({ orgID, siteID }));

	try {
		const teams = yield call(() => SuperFetch.get('/teams', {}, query));

		// Get teams from the current organisation.
		if (teams.statusCode === 200) {
			yield put(teamsActions.loadTeamsSuccess({ orgID, siteID, data: teams.data }));
		} else {
			throw Error(teams.error.message || teams.error || 'Failed to load teams.');
		}
	} catch (error) {
		yield put(teamsActions.loadTeamsError({ orgID, siteID, error: error.message }));
	}
}

export function* getTeamsSuccess() {
	yield put(teamsActions.loadAllTeamsMembers());
}

// Patches members onto each team in the active scope bucket
export function* getAllTeamsMembers() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID !== 'all' ? { siteID } : {};
		const teams = yield call(() => SuperFetch.get('/teams/getAllTeamsAllMembers', {}, query));

		if (teams.statusCode === 200) {
			yield put(teamsActions.loadAllTeamsMembersSuccess({ orgID, siteID, data: teams.data }));
		} else {
			throw Error(teams.error.message || teams.error || 'Failed to load teams.');
		}
	} catch (error) {
		yield put(teamsActions.loadAllTeamsMembersError(error));
	}
}

export function* addTeam(action) {
	try {
		// Get the data from the action payload
		const { personIDs, ...values } = action.payload.data;
		//	Get the orgID from the selected profile
		const team = yield call(() => SuperFetch.post(`/teams`, values));

		if (team.statusCode === 201 || team.statusCode === 200) {
			const teamID = team.data?.teamID;

			// If there are personIDs, add members to the team
			if (personIDs && personIDs.length > 0 && teamID) {
				yield put(teamsActions.addTeamMember({ teamID, personIDs }));
			}
			yield put(teamsActions.addTeamSuccess());
			Notification('success', 'Team created', 'Team has been added successfully.');
		} else {
			throw Error(teamErrorText(team, 'Failed to add team.'));
		}
	} catch (error) {
		yield put(teamsActions.addTeamError(error));
		Notification('error', 'Team failed', error.message);
	}
}

// 🔥Actions here after add team
export function* addTeamSuccess() {
	yield put(teamsActions.refreshTeams());
}

export function* updateTeam(action) {
	try {
		// Get the teamID and data from the action payload
		const { teamID, data } = action.payload;

		// Update the team
		const updateTeam = yield call(() => SuperFetch.patch(`/teams/${teamID}`, data));

		if (updateTeam.statusCode === 200) {
			yield put(teamsActions.updateTeamSuccess());
			Notification('success', 'Team updated', 'Team has been updated successfully.');
		} else {
			throw Error(teamErrorText(updateTeam, 'Failed to update team.'));
		}
	} catch (error) {
		yield put(teamsActions.updateTeamError(error));
		Notification('error', 'Update failed', error.message);
	}
}

// 🔥Actions here after update team
export function* updateTeamSuccess() {
	yield put(teamsActions.refreshTeams());
}

// Silent refresh — re-fetches teams without setting bucket loading flag
export function* refreshTeams() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID !== 'all' ? { siteID } : {};
		const teams = yield call(() => SuperFetch.get('/teams', {}, query));

		if (teams.statusCode === 200) {
			yield put(teamsActions.refreshTeamsSuccess({ orgID, siteID, data: teams.data }));
		}
	} catch (_) {
		// Silent — no error notification
	}
}

// ✅ Handle single or multiple team deletions via the bulk endpoint
export function* deleteTeam(action) {
	try {
		const { teamIDs } = action.payload;

		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < teamIDs.length; i += 50) chunks.push(teamIDs.slice(i, i + 50));

		yield all(
			chunks.map((chunk) =>
				call(function* () {
					const res = yield call(() => SuperFetch.delete('/teams/bulk', { teamIDs: chunk }));

					if (![200, 204].includes(res.statusCode)) {
						throw Error(res.error?.message || res.error || 'Failed to delete team(s).');
					}
				})
			)
		);

		yield put(teamsActions.deleteTeamSuccess());

		Notification(
			'success',
			teamIDs.length > 1 ? 'Teams deleted' : 'Team deleted',
			teamIDs.length > 1
				? `${teamIDs.length} teams have been permanently deleted.`
				: 'Team has been permanently deleted.'
		);
	} catch (error) {
		yield put(teamsActions.deleteTeamError(error));
		Notification('error', 'Delete failed', error.message);
	}
}

// 🔥Actions here after delete team
export function* deleteTeamSuccess() {
	yield put(teamsActions.refreshTeams());
	yield put(accessActions.refreshAccessPermissions());
}

// Fetch members for a team — patches inside the active scope bucket
export function* getTeamMembers(action) {
	try {
		const { teamID } = action.payload;
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);

		const members = yield call(() => SuperFetch.get(`/teams/${teamID}/members`));

		if (members.statusCode === 200) {
			yield put(teamsActions.loadTeamMembersSuccess({ orgID, siteID, teamID, data: members.data }));
		} else {
			throw Error(members.error.message || members.error || "Failed to load the team's people.");
		}
	} catch (error) {
		yield put(teamsActions.loadTeamMembersError(error));
	}
}

export function* addTeamMember(action) {
	try {
		// Get the teamID and personID from the action payload
		const { teamID, personIDs } = action.payload;

		// Add the member to the team
		const members = yield call(() => SuperFetch.post(`/teams/${teamID}/members`, { personIDs }));

		if (members.statusCode === 200 || members.statusCode === 201) {
			yield put(teamsActions.addTeamMemberSuccess({ teamID }));
			Notification('success', 'People added', 'People added to the team successfully.');
		} else {
			throw Error(members.error.message || members.error || 'Failed to add people to the team.');
		}
	} catch (error) {
		yield put(teamsActions.addTeamMemberError(error));
		Notification('error', 'Updating people failed', error.message);
	}
}

// 🔥Actions here after add member into the team
export function* addTeamMemberSuccess(action) {
	const { teamID } = action.payload;
	yield put(teamsActions.refreshTeamMembers({ teamID }));
	yield put(accessActions.refreshAccessPermissions());
}

export function* deleteTeamMember(action) {
	try {
		// Get the teamID and memberID from the action payload
		const { teamID, personIDs } = action.payload;

		// API request to delete the member from the team
		const members = yield call(() => SuperFetch.delete(`/teams/${teamID}/members`, { personIDs }));

		if (members.statusCode === 200) {
			yield put(teamsActions.deleteTeamMemberSuccess({ teamID }));
			Notification('success', 'People removed', 'People removed from the team successfully.');
		} else {
			throw Error(members.error.message || members.error || 'Failed to remove people from the team.');
		}
	} catch (error) {
		yield put(teamsActions.deleteTeamMemberError(error));
		Notification('error', 'Remove failed', error.message);
	}
}

// 🔥Actions here after delete member from the team
export function* deleteTeamMemberSuccess(action) {
	const { teamID } = action.payload;

	yield put(teamsActions.refreshTeamMembers({ teamID }));
	yield put(accessActions.refreshAccessPermissions());
}

// Silent refresh — re-fetches team members without setting loading flag
export function* refreshTeamMembers(action) {
	try {
		const { teamID } = action.payload;
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const members = yield call(() => SuperFetch.get(`/teams/${teamID}/members`));

		if (members.statusCode === 200) {
			yield put(teamsActions.refreshTeamMembersSuccess({ orgID, siteID, teamID, data: members.data }));
		}
	} catch (_) {
		// Silent — no error notification
	}
}

// Root saga
export default function* teamsSaga() {
	yield takeLatest(teamsActions.loadTeams, getTeams);
	yield takeLatest(teamsActions.loadTeamsSuccess, getTeamsSuccess);
	yield takeLatest(teamsActions.loadAllTeamsMembers, getAllTeamsMembers);
	yield takeLatest(teamsActions.addTeam, addTeam);
	yield takeLatest(teamsActions.addTeamSuccess, addTeamSuccess);
	yield takeLatest(teamsActions.updateTeam, updateTeam);
	yield takeLatest(teamsActions.updateTeamSuccess, updateTeamSuccess);
	yield takeLatest(teamsActions.deleteTeam, deleteTeam);
	yield takeLatest(teamsActions.deleteTeamSuccess, deleteTeamSuccess);
	yield takeEvery(teamsActions.loadTeamMembers, getTeamMembers);
	yield takeEvery(teamsActions.addTeamMember, addTeamMember);
	yield takeEvery(teamsActions.addTeamMemberSuccess, addTeamMemberSuccess);
	yield takeEvery(teamsActions.deleteTeamMember, deleteTeamMember);
	yield takeEvery(teamsActions.deleteTeamMemberSuccess, deleteTeamMemberSuccess);
	yield takeEvery(teamsActions.refreshTeamMembers, refreshTeamMembers);
	yield takeLatest(teamsActions.refreshTeams, refreshTeams);
	yield* watchActivity(teamsActions);
}
